import React, { useState, useEffect } from 'react';

interface CacheStats {
  queries: { size: number; max: number };
  api: { size: number; max: number };
  users: { size: number; max: number };
}

interface CacheMonitorProps {
  userEmail?: string;
  showInProduction?: boolean;
}

export default function CacheMonitor({ userEmail, showInProduction = false }: CacheMonitorProps) {
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCleared, setLastCleared] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isStatsLoading, setIsStatsLoading] = useState(true);

  // Only show in development unless explicitly enabled
  const shouldShow = process.env.NODE_ENV === 'development' || showInProduction;

  const fetchStats = async () => {
    try {
      setIsStatsLoading(true);
      setFetchError(null);
      const response = await fetch('/api/clear-cache');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      } else {
        setFetchError(data.error || 'Unknown error');
        console.error('❌ Cache stats failed:', data.error);
      }
    } catch (error) {
      setFetchError(error instanceof Error ? error.message : 'Network error');
      console.error('❌ Failed to fetch cache stats:', error);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const clearUserCache = async () => {
    if (!userEmail) return;
    
    setIsLoading(true);
    try {
      const response = await fetch('/api/clear-cache', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'user', userEmail })
      });
      
      const data = await response.json();
      if (data.success) {
        setLastCleared(new Date().toLocaleTimeString());
        fetchStats(); // Refresh stats
      }
    } catch (error) {
      console.error('Failed to clear cache:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (shouldShow) {
      fetchStats();
      // Refresh stats every 30 seconds
      const interval = setInterval(fetchStats, 30000);
      return () => clearInterval(interval);
    }
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white border-2 border-gray-300 rounded-lg shadow-xl p-4 text-sm z-50 min-w-[220px] hidden">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-gray-800">Cache Monitor</h4>
        <div className="flex items-center gap-2">
          {isStatsLoading ? (
            <div className="w-3 h-3 rounded-full bg-blue-400 animate-pulse shadow"></div>
          ) : stats ? (
            <>
              <div className={`w-3 h-3 rounded-full ${(stats.queries.size + stats.api.size + stats.users.size) > 0 ? 'bg-green-500' : 'bg-red-400'} shadow`}></div>
              <span className="text-gray-700 font-semibold">{stats.queries.size + stats.api.size + stats.users.size}/{stats.queries.max + stats.api.max + stats.users.max}</span>
            </>
          ) : (
            <div className="w-3 h-3 rounded-full bg-gray-400 shadow"></div>
          )}
        </div>
      </div>
      
      <div className="space-y-2 bg-gray-50 rounded p-2">
        {isStatsLoading ? (
          <div className="text-center text-gray-600">
            <div className="animate-pulse">Loading cache stats...</div>
          </div>
        ) : fetchError ? (
          <div className="text-center text-red-600 text-xs">
            Error: {fetchError}
            <button 
              onClick={fetchStats}
              className="block mx-auto mt-1 text-blue-600 hover:text-blue-800 underline"
            >
              Retry
            </button>
          </div>
        ) : stats ? (
          <>
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Queries:</span>
              <span className="font-mono text-gray-900 bg-white px-2 py-1 rounded border">{stats.queries.size}/{stats.queries.max}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">API:</span>
              <span className="font-mono text-gray-900 bg-white px-2 py-1 rounded border">{stats.api.size}/{stats.api.max}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Users:</span>
              <span className="font-mono text-gray-900 bg-white px-2 py-1 rounded border">{stats.users.size}/{stats.users.max}</span>
            </div>
          </>
        ) : (
          <div className="text-center text-gray-600 text-xs">
            No cache data available
          </div>
        )}
      </div>

      {userEmail && (
        <div className="mt-3 pt-2 border-t border-gray-200">
          <button
            onClick={clearUserCache}
            disabled={isLoading}
            className="w-full text-sm bg-red-50 hover:bg-red-100 text-red-700 font-medium px-3 py-2 rounded border border-red-200 disabled:opacity-50 transition-colors"
          >
            {isLoading ? 'Clearing...' : 'Clear My Cache'}
          </button>
          {lastCleared && (
            <div className="text-gray-600 mt-2 text-xs">
              Last cleared: {lastCleared}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-gray-200 text-center">
        <div className="text-green-600 font-medium text-xs">
          🚀 LRU Cache Active
        </div>
      </div>
    </div>
  );
} 